import { ReactNode, useEffect, useState } from "react";
import { ROTULO_UNIDADE_VALIDADORA } from "config/pre-inscricao";
import { buscarDirecionadores } from "services/pre-inscricao.service";
import {
  DirecionadorTransversal,
  getNomeCategoriaConexao,
  getNomeCategoriaEcbb,
  getRotuloSemestre,
  perguntaNorteadora,
} from "utils/direcionadores";
import type { IniciativaPreInscricao } from "utils/pre-inscricao-payload";

/** Seções do resumo; batem com as etapas do formulário para o "Editar" levar à etapa certa. */
export type SecaoResumo = "informacoes" | "direcionador" | "participantes" | "descricao";

interface ResumoPreInscricaoProps {
  iniciativa: IniciativaPreInscricao;
  /** O card da listagem já mostra o nome no cabeçalho. */
  exibirNome?: boolean;
  onEditar?: (secao: SecaoResumo) => void;
}

/**
 * Dados da pré-inscrição como foram (ou serão) gravados. Usado na conferência
 * antes do envio e nos cards da listagem, para os dois mostrarem o mesmo conteúdo.
 */
export function ResumoPreInscricao({ iniciativa, exibirNome = true, onEditar }: ResumoPreInscricaoProps) {
  const unidade = iniciativa.unidade_validadora;
  const categoriaEcbb = getNomeCategoriaEcbb(iniciativa.categoria_ecbb);
  const categoriaConexao = getNomeCategoriaConexao(iniciativa.categoria_conexao);
  const direcionador = useDescricaoDoDirecionador(iniciativa.direcionador?._id);
  const pergunta = perguntaNorteadora(iniciativa.direcionador?.nome);

  return (
    <div className="space-y-4">
      <Secao titulo="Informações da iniciativa" secao="informacoes" onEditar={onEditar}>
        <dl className="space-y-3">
          {exibirNome && <Campo rotulo="Nome da iniciativa">{iniciativa.title}</Campo>}
          <Campo rotulo="Em qual semestre?">{getRotuloSemestre(iniciativa.semestre)}</Campo>
          <Campo rotulo={ROTULO_UNIDADE_VALIDADORA}>{unidade && `${unidade.nome} (${unidade.prefixo})`}</Campo>
          {categoriaEcbb && <Campo rotulo="Categoria ECBB">{categoriaEcbb}</Campo>}
          {categoriaConexao && <Campo rotulo="Categoria">{categoriaConexao}</Campo>}
        </dl>
      </Secao>

      <Secao titulo="Direcionador transversal" secao="direcionador" onEditar={onEditar}>
        <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
          <p className="font-bold text-blue-bb text-sm">{iniciativa.direcionador?.nome ?? "—"}</p>
          {direcionador?.descricao && <p className="text-gray-bb-1 text-sm mt-1">{direcionador.descricao}</p>}
        </div>
      </Secao>

      <Secao titulo="Participantes" secao="participantes" onEditar={onEditar}>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" aria-label="Participantes">
          {(iniciativa.team ?? []).map((membro) => (
            <li key={membro._id} className="flex items-center gap-3 rounded-lg border border-gray-bb-4 p-3">
              <span
                aria-hidden="true"
                className="w-9 h-9 rounded-full bg-blue-bb text-white font-bold flex items-center justify-center flex-shrink-0"
              >
                {(membro.name || membro._id || "?")[0].toUpperCase()}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-bb-1 truncate">{membro.name || membro._id}</span>
                <span className="block text-xs text-gray-bb-2">
                  {membro._id}
                  {membro._id === iniciativa.owner && " · Responsável"}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </Secao>

      <Secao titulo="Descrição" secao="descricao" onEditar={onEditar}>
        <Texto>{iniciativa.description}</Texto>
      </Secao>

      <Secao titulo={pergunta} secao="descricao" onEditar={onEditar}>
        <Texto>{iniciativa.result}</Texto>
      </Secao>
    </div>
  );
}

/** A iniciativa grava só id e nome do direcionador; a frase vem da lista (em cache) da coleção. */
function useDescricaoDoDirecionador(id?: string) {
  const [direcionador, setDirecionador] = useState<DirecionadorTransversal>();

  useEffect(() => {
    if (!id) return;
    buscarDirecionadores()
      .then((lista) => setDirecionador(lista.find((d) => d._id === id)))
      .catch(() => setDirecionador(undefined));
  }, [id]);

  return direcionador;
}

interface SecaoProps {
  titulo: string;
  secao: SecaoResumo;
  onEditar?: (secao: SecaoResumo) => void;
  children: ReactNode;
}

function Secao({ titulo, secao, onEditar, children }: SecaoProps) {
  return (
    <section className="rounded-lg border border-gray-bb-4 p-3 sm:p-4" aria-label={titulo}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-bold text-blue-bb text-sm sm:text-base">{titulo}</h3>
        {onEditar && (
          <button
            type="button"
            onClick={() => onEditar(secao)}
            aria-label={`Editar: ${titulo}`}
            className="text-sm font-semibold text-blue-bb underline hover:opacity-80 flex-shrink-0"
          >
            Editar
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-semibold text-gray-bb-1 text-sm">{rotulo}</dt>
      <dd className="text-gray-bb-1 text-sm break-words">{children || "—"}</dd>
    </div>
  );
}

function Texto({ children }: { children?: ReactNode }) {
  return <p className="text-gray-bb-1 text-sm whitespace-pre-line break-words">{children || "—"}</p>;
}
